import { useEffect, useRef, useState } from 'react';
import { Fuel, Camera, RefreshCw, Loader2, CheckCircle2, Pencil } from 'lucide-react';
import BottomSheet from './BottomSheet';
import { zmensitObrazek } from '../../lib/obrazek';

/**
 * Photo-first tankovací sheet pro cockpit Moje směna.
 *
 * Hlavní tok: řidič vyfotí účtenku → nahraje se jako doklad ke schválení a založí
 * se čekající tankování (POST /api/me/smena/tankovani-foto). AI sken doplní litry,
 * cenu i pumpu; frontend pollováním čeká na výsledek (GET /me/tankovani/{id}/stav).
 * Když si sken není jistý / selže, sheet odhalí ruční pole (PATCH /me/tankovani/{id}).
 *
 * Tachometr, datum a typ paliva bere systém (kniha jízd / vozidlo) — řidič je nezadává.
 *
 * Fáze: 'foto' → 'processing' → 'done' | 'manual'
 * ('manual' i jako přímá cesta „Zadat ručně" bez fotky → POST /api/flotila/tankovani)
 */
export default function TankovaniSheet({
    open,
    onClose,
    onSaved,
    vozidlo,                  // { id, spz, model, km_stav, typ_paliva_id }
    user,                     // { zamestnanec_id, jmeno, prijmeni }
    smenaId = null,
    isDark,
    isMobile,
}) {
    const [faze, setFaze] = useState('foto');   // 'foto'|'processing'|'done'|'manual'
    const [fotoFile, setFotoFile] = useState(null);
    const [fotoPreview, setFotoPreview] = useState(null);
    const [tankovaniId, setTankovaniId] = useState(null);
    const [vysledek, setVysledek] = useState(null); // { litry, cena_kc_litr, dodavatel }

    const [pumpy, setPumpy] = useState([]);
    const [dodavatelId, setDodavatelId] = useState('');
    const [litry, setLitry] = useState('');
    const [cenaLitr, setCenaLitr] = useState('');
    const [platba, setPlatba] = useState('karta'); // hotovost | karta | prevod

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const cameraInputRef = useRef(null);
    const pollRef = useRef(null);

    const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

    // Reset při otevření/zavření + úklid pollingu a preview URL
    useEffect(() => {
        if (open) {
            setFaze('foto'); setFotoFile(null); setFotoPreview(null);
            setTankovaniId(null); setVysledek(null);
            setDodavatelId(''); setLitry(''); setCenaLitr(''); setPlatba('karta');
            setError(null); setSubmitting(false);
        }
        return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    }, [open]);

    // Načti seznam pump (pro ruční doplnění pumpy)
    useEffect(() => {
        if (!open) return;
        fetch('/api/dodavatele?kategorie=palivo&aktivni=1')
            .then(r => r.ok ? r.json() : [])
            .then(d => setPumpy(Array.isArray(d) ? d : []))
            .catch(() => setPumpy([]));
    }, [open]);

    // Úklid objectURL náhledu
    useEffect(() => () => { if (fotoPreview) URL.revokeObjectURL(fotoPreview); }, [fotoPreview]);

    const onPickFoto = (files) => {
        const f = files?.[0];
        if (!f) return;
        setError(null);
        if (fotoPreview) URL.revokeObjectURL(fotoPreview);
        setFotoFile(f);
        setFotoPreview(URL.createObjectURL(f));
    };

    // ── Odeslat fotku → doklad + pending tankování + spustit polling ──
    const odeslatFotku = async () => {
        if (!fotoFile) { setError('Nejdřív vyfoť účtenku.'); return; }
        setError(null); setSubmitting(true);
        try {
            const soubor = await zmensitObrazek(fotoFile);
            const fd = new FormData();
            fd.append('soubor', soubor);
            if (smenaId) fd.append('smena_id', smenaId);
            fd.append('zpusob_platby', platba);
            fd.append('foto_porizeno_at', new Date(fotoFile.lastModified).toISOString());

            const r = await fetch('/api/me/smena/tankovani-foto', {
                method: 'POST', headers: { 'X-CSRF-TOKEN': csrf() }, body: fd,
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                setError(data.error || `Nahrání selhalo (HTTP ${r.status})`);
                setSubmitting(false);
                return;
            }
            setTankovaniId(data.tankovani_id);
            setFaze('processing');
            spustitPolling(data.tankovani_id);
        } catch (e) {
            setError('Nahrání selhalo: ' + (e?.message || 'neznámá chyba'));
        } finally {
            setSubmitting(false);
        }
    };

    // Polling stavu tankování — čeká na doplnění AI skenem
    const spustitPolling = (id) => {
        let pokusy = 0;
        const maxPokusu = 15; // ~30 s
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
            pokusy++;
            try {
                const r = await fetch(`/api/me/tankovani/${id}/stav`);
                const d = await r.json().catch(() => ({}));
                if (r.ok && d.stav === 'hotovo') {
                    clearInterval(pollRef.current); pollRef.current = null;
                    setVysledek({ litry: d.litry, cena_kc_litr: d.cena_kc_litr, dodavatel: d.dodavatel });
                    setFaze('done');
                    setTimeout(() => { onSaved?.(); }, 1400);
                    return;
                }
                // Sken doběhl, ale tankování není hotové (nejistý výsledek) → ruční doplnění
                const skenDobehl = d.doklad_stav && !['nahrany', 'skenuje_se'].includes(d.doklad_stav);
                if (r.ok && d.stav === 'ceka_sken' && skenDobehl) {
                    clearInterval(pollRef.current); pollRef.current = null;
                    prejdiNaManual(d);
                    return;
                }
                if (pokusy >= maxPokusu) {
                    clearInterval(pollRef.current); pollRef.current = null;
                    prejdiNaManual(d);
                }
            } catch {
                if (pokusy >= maxPokusu) { clearInterval(pollRef.current); pollRef.current = null; prejdiNaManual({}); }
            }
        }, 2000);
    };

    const prejdiNaManual = (d = {}) => {
        if (d.litry != null) setLitry(String(d.litry));
        if (d.cena_kc_litr != null) setCenaLitr(String(d.cena_kc_litr));
        if (d.dodavatel_id) setDodavatelId(String(d.dodavatel_id));
        setFaze('manual');
    };

    // ── Uložit ruční doplnění ──
    const ulozitRucne = async () => {
        setError(null);
        const l = parseFloat(litry), c = parseFloat(cenaLitr);
        if (!l || l <= 0) { setError('Zadej množství litrů.'); return; }
        if (!c || c <= 0) { setError('Zadej cenu za litr.'); return; }
        if (!tankovaniId && !vozidlo?.id) { setError('Chybí vozidlo.'); return; }
        setSubmitting(true);
        try {
            let r;
            if (tankovaniId) {
                // Doplnění existujícího foto-tankování
                r = await fetch(`/api/me/tankovani/${tankovaniId}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf(), Accept: 'application/json' },
                    body: JSON.stringify({ litry: l, cena_kc_litr: c, dodavatel_id: dodavatelId ? Number(dodavatelId) : null }),
                });
            } else {
                // Přímé ruční tankování bez fotky (stará cesta)
                if (!vozidlo.typ_paliva_id) { setError('Vozidlo nemá nastavený typ paliva.'); setSubmitting(false); return; }
                const now = new Date();
                const pad = n => String(n).padStart(2, '0');
                const datum = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
                r = await fetch('/api/flotila/tankovani', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf(), Accept: 'application/json' },
                    body: JSON.stringify({
                        vozidlo_id: vozidlo.id, dodavatel_id: dodavatelId ? Number(dodavatelId) : null,
                        zamestnanec_id: user?.zamestnanec_id || null, datum,
                        km_stav: vozidlo.km_stav || 0, litry: l, cena_kc_litr: c,
                        typ_paliva_id: vozidlo.typ_paliva_id, poznamka: null,
                    }),
                });
            }
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                setError(data.error || (data.errors ? Object.values(data.errors).flat().join('\n') : `Uložení selhalo (HTTP ${r.status})`));
                setSubmitting(false);
                return;
            }
            onSaved?.();
        } catch (e) {
            setError('Uložení selhalo: ' + (e?.message || 'neznámá chyba'));
        } finally {
            setSubmitting(false);
        }
    };

    // ── Styling ──
    const accent = '#FF8A00';
    const labelMuted = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const textPrimary = isDark ? '#fff' : 'var(--text-primary)';
    const textMuted = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-muted)';
    const inputBg = isDark ? 'rgba(0,0,0,0.30)' : 'var(--surface-base)';
    const inputBorder = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';
    const inputStyle = {
        width: '100%', padding: '10px 12px', borderRadius: 7, fontSize: 14,
        background: inputBg, color: textPrimary, border: `1px solid ${inputBorder}`,
        boxSizing: 'border-box', outline: 'none', fontVariantNumeric: 'tabular-nums',
    };
    const btnGhost = {
        flex: 1, padding: '12px', borderRadius: 9, background: 'transparent',
        color: labelMuted, border: `1px solid ${inputBorder}`, fontSize: 14, fontWeight: 600, cursor: 'pointer',
    };
    const btnPrimary = (disabled) => ({
        flex: 2, padding: '12px', borderRadius: 9,
        background: `linear-gradient(135deg, ${accent} 0%, #F59E0B 100%)`,
        color: '#1a1a1f', border: 'none', fontSize: 14, fontWeight: 800, letterSpacing: 0.1,
        cursor: disabled ? 'wait' : 'pointer', opacity: disabled ? 0.5 : 1, boxShadow: `0 6px 20px ${accent}55`,
    });

    return (
        <BottomSheet open={open} onClose={onClose} isDark={isDark} isMobile={isMobile} title={null}>
            {/* Hlavička */}
            <div style={{ padding: '14px 18px 12px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{
                    width: 44, height: 44, borderRadius: 9, background: `${accent}1A`, color: accent,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                    <Fuel size={22} />
                </span>
                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 18, fontWeight: 800, color: textPrimary }}>Tankování</span>
                    <span style={{ fontSize: 12, color: textMuted, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                        {vozidlo?.spz || '—'}{vozidlo?.model && <> · {vozidlo.model}</>}
                        {typeof vozidlo?.km_stav === 'number' && <> · {vozidlo.km_stav.toLocaleString('cs-CZ')} km</>}
                    </span>
                </div>
            </div>

            <div style={{ padding: '0 18px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
                    onChange={e => onPickFoto(e.target.files)} style={{ display: 'none' }} />

                {/* ── FÁZE: FOTO ── */}
                {faze === 'foto' && (
                    <>
                        {!fotoPreview ? (
                            <button type="button" onClick={() => cameraInputRef.current?.click()}
                                style={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
                                    padding: '32px 16px', borderRadius: 10, cursor: 'pointer',
                                    background: `${accent}12`, border: `2px dashed ${accent}66`, color: accent,
                                }}>
                                <Camera size={34} />
                                <span style={{ fontSize: 15, fontWeight: 800 }}>Vyfotit účtenku</span>
                                <span style={{ fontSize: 12, color: textMuted, textAlign: 'center' }}>
                                    Litry, cenu i pumpu doplníme automaticky ze skenu.
                                </span>
                            </button>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <img src={fotoPreview} alt="Náhled účtenky"
                                    style={{ width: '100%', maxHeight: 320, objectFit: 'contain', borderRadius: 9, background: '#000' }} />
                                <button type="button" onClick={() => cameraInputRef.current?.click()}
                                    style={{ ...btnGhost, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                    <RefreshCw size={14} /> Přefotit
                                </button>
                            </div>
                        )}

                        {/* Volba platby (default Karta) */}
                        <div>
                            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: labelMuted }}>Zaplaceno</span>
                            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                {[['karta', 'Kartou'], ['hotovost', 'Hotově'], ['prevod', 'Převodem']].map(([key, label]) => {
                                    const on = platba === key;
                                    return (
                                        <button key={key} type="button" onClick={() => setPlatba(key)}
                                            style={{ flex: 1, padding: '10px', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                                                background: on ? `${accent}22` : inputBg, color: on ? accent : textMuted,
                                                border: `1px solid ${on ? accent + '88' : inputBorder}` }}>
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {error && <ErrorBox error={error} />}

                        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                            <button type="button" onClick={onClose} style={btnGhost}>Zrušit</button>
                            <button type="button" onClick={odeslatFotku} disabled={submitting || !fotoFile} style={btnPrimary(submitting || !fotoFile)}>
                                {submitting ? 'Odesílám…' : 'Odeslat účtenku'}
                            </button>
                        </div>

                        <button type="button" onClick={() => setFaze('manual')}
                            style={{ background: 'none', border: 'none', color: labelMuted, fontSize: 12, cursor: 'pointer', textDecoration: 'underline', alignSelf: 'center' }}>
                            <Pencil size={11} style={{ display: 'inline', marginRight: 4 }} /> Zadat ručně bez fotky
                        </button>
                    </>
                )}

                {/* ── FÁZE: ZPRACOVÁNÍ ── */}
                {faze === 'processing' && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '28px 16px' }}>
                        <Loader2 size={34} className="animate-spin" style={{ color: accent }} />
                        <span style={{ fontSize: 15, fontWeight: 700, color: textPrimary }}>Čtu účtenku…</span>
                        <span style={{ fontSize: 12, color: textMuted, textAlign: 'center' }}>
                            Účtenka je odeslaná ke schválení. Za chvíli doplníme litry a cenu.
                        </span>
                        <button type="button" onClick={onClose} style={{ ...btnGhost, flex: 'none', marginTop: 4 }}>
                            Zavřít (dokončí se na pozadí)
                        </button>
                    </div>
                )}

                {/* ── FÁZE: HOTOVO ── */}
                {faze === 'done' && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '28px 16px' }}>
                        <CheckCircle2 size={40} style={{ color: '#22C55E' }} />
                        <span style={{ fontSize: 16, fontWeight: 800, color: textPrimary }}>Natankováno ✓</span>
                        <span style={{ fontSize: 14, color: textMuted, fontVariantNumeric: 'tabular-nums' }}>
                            {vysledek?.litry != null && <>{Number(vysledek.litry).toLocaleString('cs-CZ')} l</>}
                            {vysledek?.cena_kc_litr != null && <> · {Number(vysledek.cena_kc_litr).toLocaleString('cs-CZ')} Kč/l</>}
                        </span>
                        {vysledek?.dodavatel && <span style={{ fontSize: 12, color: textMuted }}>{vysledek.dodavatel}</span>}
                    </div>
                )}

                {/* ── FÁZE: RUČNÍ DOPLNĚNÍ ── */}
                {faze === 'manual' && (
                    <>
                        {tankovaniId && (
                            <div style={{
                                padding: '8px 12px', borderRadius: 7, fontSize: 12,
                                background: 'rgba(245,158,11,0.12)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.3)',
                            }}>
                                Účtenku se nepodařilo přečíst automaticky — doplň prosím litry a cenu.
                            </div>
                        )}
                        <Field label="Pumpa" labelMuted={labelMuted}>
                            <select value={dodavatelId} onChange={e => setDodavatelId(e.target.value)} style={inputStyle}>
                                <option value="">— vyber pumpu (volitelné) —</option>
                                {pumpy.map(p => <option key={p.id} value={p.id}>{p.nazev}</option>)}
                            </select>
                        </Field>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <Field label="Litry" labelMuted={labelMuted} required>
                                <input type="number" inputMode="decimal" step="0.01" min="0.01" value={litry}
                                    onChange={e => setLitry(e.target.value)} placeholder="0,00" style={{ ...inputStyle, fontWeight: 700 }} />
                            </Field>
                            <Field label="Cena/l" labelMuted={labelMuted} required>
                                <input type="number" inputMode="decimal" step="0.01" min="0.01" value={cenaLitr}
                                    onChange={e => setCenaLitr(e.target.value)} placeholder="0,00" style={{ ...inputStyle, fontWeight: 700 }} />
                            </Field>
                        </div>

                        {error && <ErrorBox error={error} />}

                        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                            <button type="button" onClick={onClose} style={btnGhost}>Zrušit</button>
                            <button type="button" onClick={ulozitRucne} disabled={submitting} style={btnPrimary(submitting)}>
                                {submitting ? 'Ukládám…' : 'Uložit tankování'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </BottomSheet>
    );
}

function ErrorBox({ error }) {
    return (
        <div style={{
            padding: '8px 12px', borderRadius: 7, fontSize: 12, whiteSpace: 'pre-line',
            background: 'rgba(239,68,68,0.12)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)',
        }}>{error}</div>
    );
}

function Field({ label, children, labelMuted, required }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: labelMuted }}>
                {label}{required && <span style={{ color: '#EF4444' }}> *</span>}
            </span>
            {children}
        </label>
    );
}
